import { Button, Dropdown, Layout, Menu, Drawer, Tooltip } from 'antd';
import { useState, useEffect } from 'react';
import './layout.css'
import {
    MenuOutlined,
    SearchOutlined,
    CalculatorOutlined,
    CreditCardOutlined,
    FileTextOutlined,
    MessageOutlined,
    MedicineBoxOutlined,
    BellOutlined,
    BgColorsOutlined,
    BugOutlined,
    ScheduleOutlined,
    SettingOutlined,
    AppstoreOutlined,
} from "@ant-design/icons";
import PriceQuoteModal from "../../../../app/price-quote/PriceQuoteModal";
import { useSearch } from "../../../context/SearchContext";
import Flex from "../../Flex";
import Logo from "./Logo";
import ProfileDropdown from "./ProfileDropdown";
import Footer from "./Footer";
import BottomNav, { BOTTOM_NAV_HEIGHT } from "./BottomNav";
import { useTranslation } from "react-i18next";
import { Route, useHistory, useLocation } from "react-router-dom";
import { ANALYTICS_BOARD_PERMISSIONS, PERMISSIONS, useUser } from "../../../context/UserContext";
import { BrowserView, isMobile, MobileView } from "react-device-detect";
import { CalendarOutlined, TableOutlined } from "@ant-design/icons";
import { GiMedicalPack } from "react-icons/gi";
import { FaClinicMedical, FaCashRegister } from "react-icons/fa";
import { AiOutlinePlus, AiOutlineApartment, AiOutlineAreaChart, AiFillDatabase } from "react-icons/ai";
import { BsFillCalendarDateFill, BsCashStack, BsFillPersonBadgeFill } from 'react-icons/bs';
import { HiUserGroup } from 'react-icons/hi';
import NotificationMenu from "../../../../app/NotificationMenu";
import { Branding } from "../../../service/BrandingService";

const { Header } = Layout;

const { SubMenu } = Menu;

const Icon = ({ icon }) => {
    return (
        <div className={'anticon antitem'}>
            {icon}
        </div>
    )
}

const DefinitionsIcon = () => {
    return <Icon icon={<AiFillDatabase />} />
}

const DashboardIcon = () => {
    return <Icon icon={<AiOutlineAreaChart />} />
}

const CashRegisterIcon = () => {
    return <Icon icon={<FaCashRegister />} />
}

const DoctorIcon = () => {
    return <Icon icon={<GiMedicalPack />} />
}

const UnitIcon = () => {
    return <Icon icon={<AiOutlineApartment />} />
}

const OperationIcon = () => {
    return <Icon icon={<AiOutlinePlus />} />
}

const InstitutionIcon = () => {
    return <Icon icon={<FaClinicMedical />} />
}

const NewAppointmentIcon = () => {
    return <Icon icon={<BsFillCalendarDateFill />} />
}

const Expense = () => {
    return <Icon icon={<BsCashStack />} />
}

const Patient = () => {
    return <Icon icon={<BsFillPersonBadgeFill />} />
}

const UserIcon = () => {
    return <Icon icon={<HiUserGroup />} />
}

export default function MainLayout({ children }) {
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const key = window.location.pathname;
    const { hasPermission } = useUser()
    const [menuVisible, setMenuVisible] = useState(false);
    const { openSearch } = useSearch();
    const [quoteOpen, setQuoteOpen] = useState(false);
    // Mac kullanıcıları için ⌘, diğerleri için Ctrl
    const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPod|iPad/.test(navigator.platform);
    const shortcutLabel = isMac ? '⌘E' : 'Ctrl+E';
    const quoteShortcutLabel = isMac ? '⌘D' : 'Ctrl+D';
    // Fiyat Teklifi baştan sona fiyat gösterdiği için ayrı bir izni yok; fiyat görme izniyle açılır.
    const canUsePriceQuote = hasPermission(PERMISSIONS.VIEW_PRICE);

    // Panolar (kasa + analiz sekmeleri) dar ekranda her pikseli kullanıyor; alt bar orada gizlenir.
    // Bunlara izni olmayanda /app/dashboard Metabase panosunu açıyor — bar yalnız panolar sayfasında kalkar.
    const hasDashboardHub = hasPermission(PERMISSIONS.CASH_DASHBOARD) || ANALYTICS_BOARD_PERMISSIONS.some(hasPermission);
    const hideBottomNav = location.pathname.replace(/\/+$/, '') === '/app/dashboard' && hasDashboardHub;

    // Cmd/Ctrl+D → Fiyat Teklifi modal'ı aç (browser bookmark shortcut override edilir).
    // İzin yoksa dinleyici hiç kurulmaz, kısayol tarayıcıya geri bırakılır.
    useEffect(() => {
        if (!canUsePriceQuote) return;

        const onKey = (e) => {
            const isMeta = e.metaKey || e.ctrlKey;
            if (isMeta && !e.shiftKey && !e.altKey && (e.key === 'd' || e.key === 'D')) {
                e.preventDefault();
                setQuoteOpen(true);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [canUsePriceQuote]);

    // Header rengi MARKADAN gelir (DB). Kiracı belirlemediyse marka slate'i kullanılır.
    // Metin rengi luma'ya göre otomatik hesaplanır → açık renkli header'da da okunur kalır.
    const headerColor = Branding.headerColor();

    // Simple helper to check if color is dark to set text color
    const isDark = (color) => {
        if (!color || color.startsWith('rgba') || color === 'transparent') return false;
        const c = color.substring(1);      // strip #
        const rgb = parseInt(c, 16);   // convert rrggbb to decimal
        const r = (rgb >> 16) & 0xff;  // extract red
        const g = (rgb >> 8) & 0xff;  // extract green
        const b = (rgb >> 0) & 0xff;  // extract blue
        const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b; // per ITU-R BT.709
        return luma < 128;
    }

    const textColor = isDark(headerColor) ? '#ffffff' : '#595959';
    const menuTheme = isDark(headerColor) ? 'dark' : 'light';

    // Çekmeceler (mobil menü, profil) portal ile <body> altına render edilir; #site-header'ın
    // üzerindeki inline değişkenleri göremezler. Header rengini köke yayınlıyoruz ki çekmece
    // başlığı da kiracının header rengiyle aynı olsun — logo o zemin için tasarlandı.
    useEffect(() => {
        const root = document.documentElement;
        root.style.setProperty('--header-bg', headerColor);
        root.style.setProperty('--header-fg', textColor);
    }, [headerColor, textColor]);

    // Menü öğeleri gerçek <a href> linkidir: orta tık / Ctrl(⌘)+tık / Shift+tık tarayıcının kendi
    // davranışıyla yeni sekme/pencere açar. Düz sol tıkta tam sayfa yüklemesini engelleyip
    // SPA içinde gezinir (history.push Menu onClick'te).
    const isModifiedClick = (e) => !!e && (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey);
    const navLink = (path, text) => (
        <a
            href={path}
            onClick={(e) => {
                if (!isModifiedClick(e)) e.preventDefault();
            }}
        >
            {text}
        </a>
    );
    const onMenuNavigate = (item) => {
        if (isModifiedClick(item.domEvent)) return false; // tarayıcı yeni sekmede açıyor
        history.push(item.key);
        return true;
    };

    const getMenuItem = (key, text) => {
        return (
            <Menu.Item key={key} icon={<CalendarOutlined />}>
                <a href={key}>{text}</a>
            </Menu.Item>
        )
    }

    // Hasta Sıralama ekranının rotası izinsiz; menüde eskiden olduğu gibi yalnız tanım/sistem
    // yetkisi olanlara görünür.
    const hasSystemPermission = () => {
        return hasPermission(PERMISSIONS.FIRM_UNIT_MANAGER) ||
            hasPermission(PERMISSIONS.FIRM_DOCTOR_MANAGER) ||
            hasPermission(PERMISSIONS.FIRM_OPERATION_MANAGER) ||
            hasPermission(PERMISSIONS.FIRM_CASH_REGISTER) ||
            hasPermission(PERMISSIONS.FIRM_USER_MANAGER) ||
            hasPermission(PERMISSIONS.APPLOG_VIEW) ||
            hasPermission(PERMISSIONS.ENABIZ_OPERATION_TRACKING) ||
            hasPermission(PERMISSIONS.NOTIFICATION_SETTINGS_MANAGER) ||
            hasPermission(PERMISSIONS.BRANDING_MANAGE) ||
            hasPermission(PERMISSIONS.APPOINTMENT_TYPE_MANAGER)
    }

    // Açılır menüler veriyle tanımlanır: her grup yalnız görünür öğesi varsa çizilir,
    // alt menü de yalnız görünür grubu varsa. Yeni ekran eklemek = ilgili gruba bir satır.
    //   Ön Büro:  günlük işte kullanılan hasta ve kasa ekranları
    //   Tanımlar: klinik/finans ana verisi (günlük işte bakılan listeler)
    //   Sistem:   kullanıcı yönetimi, kayıtlar, ayarlar
    const frontOfficeGroups = [
        {
            key: 'front-office-patient',
            title: t('Patient'),
            items: [
                { path: `/app/patients/grid`, label: t('Patients'), icon: <Patient />, visible: hasPermission(PERMISSIONS.APPOINTMENT_VIEW) },
            ]
        },
        {
            key: 'front-office-finance',
            title: t('Finance'),
            items: [
                { path: `/app/expenses`, label: t('Expenses'), icon: <Expense />, visible: hasPermission(PERMISSIONS.EXPENSE_VIEW) },
                { path: `/app/cash-view`, label: t('Cash Register'), icon: <CashRegisterIcon />, visible: hasPermission(PERMISSIONS.CASH_REGISTER_VIEW) },
            ]
        },
    ];

    const definitionGroups = [
        {
            key: 'definitions-clinic',
            title: t('Clinic'),
            items: [
                { path: `/app/system/operations`, label: t('Operations'), icon: <OperationIcon />, visible: true },
                { path: `/app/system/institutions`, label: t('Institutions'), icon: <InstitutionIcon />, visible: true },
                { path: `/app/system/units`, label: t('Units'), icon: <UnitIcon />, visible: hasPermission(PERMISSIONS.FIRM_UNIT_MANAGER) },
                { path: `/app/system/doctors`, label: t('Doctors'), icon: <DoctorIcon />, visible: hasPermission(PERMISSIONS.FIRM_DOCTOR_MANAGER) },
                { path: `/app/system/appointment-types`, label: t('Appointment Types'), icon: <Icon icon={<ScheduleOutlined />} />, visible: hasPermission(PERMISSIONS.APPOINTMENT_TYPE_MANAGER) },
            ]
        },
        {
            key: 'definitions-finance',
            title: t('Finance'),
            items: [
                { path: `/app/system/cash-registers`, label: t('Cash Registers'), icon: <CashRegisterIcon />, visible: hasPermission(PERMISSIONS.FIRM_CASH_REGISTER) },
                { path: `/app/system/pos-devices`, label: t('POS Devices'), icon: <Icon icon={<CreditCardOutlined />} />, visible: hasPermission(PERMISSIONS.FIRM_CASH_REGISTER) },
            ]
        },
    ];

    const systemGroups = [
        {
            key: 'system-users',
            title: t('User Management'),
            items: [
                { path: `/app/system/users`, label: t('Users'), icon: <UserIcon />, visible: hasPermission(PERMISSIONS.FIRM_USER_MANAGER) },
                { path: `/app/system/roles`, label: t('Roles'), icon: <UserIcon />, visible: hasPermission(PERMISSIONS.FIRM_USER_MANAGER) },
            ]
        },
        {
            key: 'system-logs',
            title: t('Logs & Tracking'),
            items: [
                { path: `/app/applog/grid`, label: t('Logs'), icon: <Icon icon={<FileTextOutlined />} />, visible: hasPermission(PERMISSIONS.APPLOG_VIEW) },
                { path: `/app/system/system-logs`, label: t('System Logs'), icon: <Icon icon={<BugOutlined />} />, visible: hasPermission(PERMISSIONS.APPLOG_VIEW) },
                { path: `/app/sms-logs/grid`, label: t('SMS Logs'), icon: <Icon icon={<MessageOutlined />} />, visible: hasPermission(PERMISSIONS.APPLOG_VIEW) },
                { path: `/app/system/enabiz-operations`, label: t('Enabiz Operation Tracking'), icon: <Icon icon={<MedicineBoxOutlined />} />, visible: hasPermission(PERMISSIONS.ENABIZ_OPERATION_TRACKING) },
            ]
        },
        {
            key: 'system-settings',
            title: t('Settings'),
            items: [
                { path: `/app/system/notification-settings`, label: t('Notification Settings'), icon: <Icon icon={<BellOutlined />} />, visible: hasPermission(PERMISSIONS.NOTIFICATION_SETTINGS_MANAGER) },
                { path: `/app/system/branding`, label: t('Branding'), icon: <Icon icon={<BgColorsOutlined />} />, visible: hasPermission(PERMISSIONS.BRANDING_MANAGE) },
            ]
        },
        {
            key: 'system-screens',
            title: t('Screens'),
            items: [
                { path: `/app/patient-queue/grid`, label: t('Patient Queue'), icon: <Patient />, visible: hasSystemPermission() },
            ]
        },
    ];

    const visibleGroups = (groups) => groups
        .map(g => ({ ...g, items: g.items.filter(i => i.visible) }))
        .filter(g => g.items.length > 0);

    const renderSubMenu = (key, title, icon, groups) => {
        const visible = visibleGroups(groups);
        if (visible.length === 0) return null;

        return (
            <SubMenu
                key={key}
                title={title}
                icon={icon}
                /* antd v5'te popupClassName Menu'de değil SubMenu'de çalışır; açılır panel
                   <body> altına portal ile gider, header zemini için sınıf veriyoruz (layout.css).
                   Birden çok grup varsa panel iki sütuna yayılır — tek uzun liste olmasın. */
                popupClassName={`header-menu-popup${visible.length > 1 ? ' header-menu-popup--columns' : ''}`}
            >
                {visible.map(g => (
                    <Menu.ItemGroup key={g.key} title={g.title}>
                        {g.items.map(i => (
                            <Menu.Item key={i.path} icon={i.icon}>{navLink(i.path, i.label)}</Menu.Item>
                        ))}
                    </Menu.ItemGroup>
                ))}
            </SubMenu>
        );
    }

    const getMenu = () => {
        return (
            <>
                {/* Tek giriş: kasa/analiz panosu izni varsa /app/dashboard sekmeli panolar sayfasını,
                    yoksa Metabase panosunu açar. (Metabase panosu ayrıca /app/dashboard/analytics adresinde.) */}
                {(hasDashboardHub || hasPermission(PERMISSIONS.DASHBOARD)) &&
                    <Menu.Item key={`/app/dashboard`} icon={<DashboardIcon />}>{navLink(`/app/dashboard`, t('Dashboard'))}</Menu.Item>}

                {hasPermission(PERMISSIONS.APPOINTMENT_VIEW) && <Menu.Item key={`/app/appointments/calendar`} icon={<CalendarOutlined />}>{navLink(`/app/appointments/calendar`, t('Calendar'))}</Menu.Item>}
                {hasPermission(PERMISSIONS.APPOINTMENT_VIEW) && <Menu.Item key={`/app/appointments/grid`} icon={<TableOutlined />}>{navLink(`/app/appointments/grid`, t('Appointments'))}</Menu.Item>}

                {renderSubMenu('front-office', t('Front Office'), <Icon icon={<AppstoreOutlined />} />, frontOfficeGroups)}
                {renderSubMenu('definitions', t('Definitions'), <DefinitionsIcon />, definitionGroups)}
                {renderSubMenu('system', t('System'), <Icon icon={<SettingOutlined />} />, systemGroups)}
            </>
        )
    }

    const getMobileMenu = () => {
        return (
            <Menu
                style={{ marginLeft: 16, minWidth: 128 }}
                onClick={onMenuNavigate}
                theme="dark"
                defaultSelectedKeys={[key]}
            >
                {getMenu()}
            </Menu>
        )
    }

    return (
        <Layout className={`app-shell${hideBottomNav ? ' app-shell--no-bottom-nav' : ''}`}>
            <MobileView>
                <Header id={'site-header'} style={{
                    position: 'fixed',
                    zIndex: 100,
                    width: '100%',
                    height: 64,
                    padding: '0 16px',
                    backgroundColor: headerColor,
                    backdropFilter: 'blur(8px)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    color: textColor,
                    // header rengi kiracıya göre değişiyor → stiller CSS değişkenine bağlanır (layout.css)
                    '--header-fg': textColor,
                    '--header-bg': headerColor,
                }}>
                    <Flex alignItems={'center'} flexWrap={'nowrap'} justifyContent={'space-between'} style={{ width: '100%', height: '100%' }}>
                        <Button
                            type="text"
                            shape="circle"
                            icon={<MenuOutlined style={{ color: textColor, fontSize: '20px' }} />}
                            onClick={() => setMenuVisible(true)}
                            style={{ height: 40, width: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        />
                        <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                            <Logo />
                        </div>
                        <ProfileDropdown isMobile={true} />
                    </Flex>
                </Header>
                <Drawer
                    title={<Logo />}
                    placement="left"
                    closable={true}
                    onClose={() => setMenuVisible(false)}
                    open={menuVisible}
                    /* bodyStyle v5'te kaldırıldı (deprecation uyarısı basıyordu) */
                    styles={{ body: { padding: 0 } }}
                    /* CSS ile hesaplanır: ekran döndüğünde/yeniden boyutlandığında da doğru
                       (Math.min(window.innerWidth) ilk render'da donuyordu) */
                    width="min(86vw, 320px)"
                    className="mobile-nav-drawer nav-drawer"
                >
                    <Menu
                        mode="inline"
                        theme="light"
                        onClick={(item) => {
                            if (onMenuNavigate(item)) setMenuVisible(false)
                        }}
                        /* defaultSelectedKeys yalnız ilk mount'ta okunur; çekmece açık kalıp
                           rota değişince seçim eski satırda takılı kalıyordu. */
                        selectedKeys={[key]}
                        style={{ border: 'none' }}
                    >
                        {getMenu()}
                    </Menu>
                </Drawer>
                {!hideBottomNav && <BottomNav onOpenQuote={canUsePriceQuote ? () => setQuoteOpen(true) : undefined} />}
            </MobileView>
            <BrowserView>
                <Header id={'site-header'} style={{
                    position: 'fixed',
                    zIndex: 100,
                    width: '100%',
                    backgroundColor: headerColor,
                    backdropFilter: 'blur(8px)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    color: textColor,
                    '--header-fg': textColor,
                    '--header-bg': headerColor,
                }}>
                    <Flex alignItems={'center'}>
                        <Logo />
                        <Menu
                            onClick={onMenuNavigate}
                            theme={menuTheme}
                            mode="horizontal"
                            selectedKeys={[location.pathname]}
                            style={{ marginLeft: 12, flex: 1, minWidth: 0, background: 'transparent', borderBottom: 'none', color: textColor }}
                        >
                            {getMenu()}
                        </Menu>
                        {/* Spotlight-tarzı arama — Cmd/Ctrl+E kısayolu ile de açılır */}
                        <Tooltip title={`${t('Search')} (${shortcutLabel})`}>
                            <Button
                                type="text"
                                className="header-action"
                                aria-label={t('Search')}
                                icon={<SearchOutlined style={{ fontSize: 17 }} />}
                                onClick={openSearch}
                            />
                        </Tooltip>
                        {/* Fiyat Teklifi — Cmd/Ctrl+D kısayolu */}
                        {canUsePriceQuote && (
                            <Tooltip title={`${t('Price Quote')} (${quoteShortcutLabel})`}>
                                <Button
                                    type="text"
                                    className="header-action"
                                    aria-label={t('Price Quote')}
                                    icon={<CalculatorOutlined style={{ fontSize: 17 }} />}
                                    onClick={() => setQuoteOpen(true)}
                                />
                            </Tooltip>
                        )}
                        <NotificationMenu />
                        <ProfileDropdown />
                    </Flex>
                </Header>
            </BrowserView>
            <Layout
                className="site-layout"
                style={{
                    // Mobilde bottom nav var; içeriğin üzerine binmemesi için altta yer bırak
                    paddingBottom: isMobile && !hideBottomNav ? BOTTOM_NAV_HEIGHT + 8 : undefined,
                }}
            >
                {children}
            </Layout>
            {/* Footer site-layout'un DIŞINDA: sayfalar Sider kullanıyor, antd o Layout'a
                .ant-layout-has-sider verip ekseni YATAY yapıyor ve içerideki footer sağa savruluyor. */}
            <Footer />

            {/* Global Fiyat Teklifi Modal — header butonu veya Cmd/Ctrl+D ile açılır */}
            {canUsePriceQuote && (
                <PriceQuoteModal
                    open={quoteOpen}
                    onClose={() => setQuoteOpen(false)}
                />
            )}
        </Layout>
    )
}
